import { ageGroup1, ageGroup2, ageGroup3, ageGroup4 } from '@/assets/messages';
import { AnimatedHeadingType } from '@/components/animated-type/animated-heading-type';
import { Text } from '@/components/animated-type/text';
import { AnnotatedType } from '@/components/annotated-type';
import { StoryHeader, StoryTagline, StoryTheme, StoryTitle } from '@/components/microstory/header';
import { Outro } from '@/components/microstory/outro';
import { StoriesNavigation } from '@/components/microstory/stories-navigation';
import { Theme, Themes } from '@/components/microstory/theme';
import { SeoHead } from '@/components/seo-head';
import { GoToSources2023Chapter1 } from '@/components/stories/go-to-sources/chapter-1';
import { GoToSources2023Chapter2 } from '@/components/stories/go-to-sources/chapter-2';
import { GoToSources2023Chapter3 } from '@/components/stories/go-to-sources/chapter-3';
import { GoToSources2023Chapter4 } from '@/components/stories/go-to-sources/chapter-4';
import data from '@/data/go-to-sources.json';
import { createGetServerSideProps } from '@/utils/create-get-server-side-props';
import { getStoryProps } from '@/utils/get-story-props';
import { useLayout } from '@/utils/use-layout';
import { useSharingImage } from '@/utils/use-sharing-image';
import type { I18n } from '@lingui/core';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { InferGetServerSidePropsType } from 'next';

export enum AgeGroup {
  younger = 'younger',
  older = 'older',
}

export enum MediaType {
  digital = 'digital',
  analog = 'analog',
}

export const getAgeGroupLabel = (i18n: I18n, key: keyof AgeGroups) => {
  switch (key) {
    case '15_24':
      return ageGroup1(i18n);
    case '25_39':
      return ageGroup2(i18n);
    case '40_64':
      return ageGroup3(i18n);
    case '65plus':
      return ageGroup4(i18n);
  }
};

export const getMediaLabel = (i18n: I18n, key: MediaType) => {
  switch (key) {
    case 'digital':
      return t(i18n)({ message: 'digital sources', id: 'common.digital' });
    case 'analog':
      return t(i18n)({ message: 'traditional sources', id: 'common.analog' });
  }
};

export const getServerSideProps = createGetServerSideProps(
  async () => ({ data: data as GoToSourcesData }),
  getStoryProps('go-to-sources', 'is-scrolling-believing')
);

export default function GoToSources(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { storySlug, baseUrl, data, sharingUrl, linkedStoryIds, relatedStoryId } = props;

  useLayout('white');
  useLingui();

  const sharingImage = useSharingImage('go-to-sources');
  const pageTitle = t({ message: 'Go-to sources', id: 'go-to-sources.story-title' });
  const pageDescription = t({
    id: 'go-to-sources.description',
    message: 'Information is everywhere. Where do you find yours?',
  });

  return (
    <>
      <SeoHead
        baseUrl={baseUrl}
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        sharingImage={sharingImage}
      />

      <Theme theme={Themes.sky} hasBackButton>
        <StoryHeader backgroundId={storySlug} css={{ backgroundPosition: 'right bottom' }}>
          <StoryTheme>
            <Trans id="go-to-sources-2023.story-theme">Information</Trans>
          </StoryTheme>
          <StoryTitle>{pageTitle}</StoryTitle>
          <StoryTagline>
            <Trans id="go-to-sources-2023.story-tagline">
              <Text type="heading">
                Information is <AnimatedHeadingType>everywhere.</AnimatedHeadingType> Where do you
                find yours?
              </Text>
            </Trans>
          </StoryTagline>
        </StoryHeader>

        <GoToSources2023Chapter1
          traditionalData={data['use_traditional_media']}
          digitalData={data['use_digital_sources']}
        />
        <GoToSources2023Chapter2
          digitalVsTraditionalData={data['digital_vs_analog']}
          socialMediaData={data['use_social_media']}
        />
        <GoToSources2023Chapter3 />
        <GoToSources2023Chapter4 data={data['primary_sources_for_young_people_chart_ready']} />

        <Outro
          css={{
            marginBlockStart: '25vh',
            '@bp11': {
              marginBlockStart: 0,
            },
          }}
          relatedStoryId={relatedStoryId}
          sharing={{
            url: sharingUrl,
            title: t({ id: 'go-to-sources-2023.sharing.title', message: 'Go-to sources' }),
            text: t({
              id: 'go-to-sources-2023.sharing.text',
              message: 'Information is everywhere. Where do you find yours?',
            }),
          }}
        >
          <Trans id="go-to-sources-2023.wrap-up">
            How do you think a reliance on social media platforms{' '}
            <AnnotatedType>shapes young people's experiences and attitudes?</AnnotatedType>
          </Trans>
        </Outro>
      </Theme>

      <StoriesNavigation storyIds={linkedStoryIds} />
    </>
  );
}
